import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
} from '@/components/ui/navigation-menu';
import { SEARCH } from '@/constants/app.constants';
import { CANONICAL_GENRES } from '@/constants/genres';
import { getActiveSeason } from '@/utils/season';

/**
 * Four columns, each answering one question a visitor has, every list capped
 * at eight. Long tails link to the pages that already hold them.
 *
 * Lists are ranked by sequence count in production on 2026-09-10 (genres via
 * /api/genres, props via sequence_props). Prop names must be the canonical
 * `props.name` values: the search page filters on `props_used_names`, so a
 * label that is not a real prop name returns nothing.
 */

const NEWEST = encodeURIComponent(SEARCH.SORT_OPTIONS.NEWEST_FIRST);

// Slugs only; names come from the canonical taxonomy so a typo cannot ship.
const TOP_GENRE_SLUGS = ['rock', 'pop', 'soundtrack', 'hip-hop', 'faith-and-religion', 'country', 'dance-electronic', 'metal'];
export const TOP_GENRES = TOP_GENRE_SLUGS.map((slug) => ({
  slug,
  name: CANONICAL_GENRES.find((genre) => genre.slug === slug)?.name ?? slug,
}));

export const TOP_PROPS = ['House Outline', 'Arches', 'Matrix', 'Trees', 'Mega Tree', 'Snowflakes', 'Stars', 'Windows'];

const propSearch = (name: string) => `/sequences/search?props_used=${encodeURIComponent(name)}`;
const categorySearch = (slug: string) => `/sequences/search?props_category=${encodeURIComponent(slug)}`;

interface MenuLinkItem {
  label: string;
  to: string;
  /** A "there is more" link, drawn muted with an arrow. */
  more?: boolean;
}

interface MenuGroup {
  heading: string;
  items: MenuLinkItem[];
}

function holidayColumn(): MenuGroup[] {
  const season = getActiveSeason();
  const holidays: Array<'Halloween' | 'Christmas'> =
    season === 'halloween' ? ['Halloween', 'Christmas'] : ['Christmas', 'Halloween'];
  return [
    {
      heading: 'Holiday',
      items: holidays.map((holiday) => ({ label: holiday, to: `/sequences/holiday/${holiday}` })),
    },
    {
      heading: 'Just added',
      items: holidays.map((holiday) => ({
        label: `Newest ${holiday.toLowerCase()}`,
        to: `/sequences/holiday/${holiday}?sort=${NEWEST}`,
      })),
    },
    {
      heading: 'By Software',
      items: [
        { label: 'xLights', to: '/sequences/xLights' },
        { label: 'Light-O-Rama', to: '/sequences/Light-O-Rama' },
      ],
    },
  ];
}

const PROPS_COLUMN: MenuGroup[] = [
  {
    heading: 'By Prop',
    items: [
      ...TOP_PROPS.map((name) => ({ label: name, to: propSearch(name) })),
      { label: 'Christmas figures', to: categorySearch('christmas-figures'), more: true },
      { label: 'Halloween figures', to: categorySearch('halloween-figures'), more: true },
    ],
  },
];

const GENRE_COLUMN: MenuGroup[] = [
  {
    heading: 'Genre',
    items: [
      ...TOP_GENRES.map((genre) => ({ label: genre.name, to: `/sequences/genre/${genre.slug}` })),
      { label: 'All genres', to: '/sequences/genres', more: true },
    ],
  },
];

const QUICK_COLUMN: MenuGroup[] = [
  {
    heading: 'Quick picks',
    items: [
      { label: 'Free', to: '/sequences/free' },
      { label: 'Newest', to: `/sequences/search?sort=${NEWEST}` },
      { label: 'Most upvoted', to: `/sequences/search?sort=${encodeURIComponent(SEARCH.SORT_OPTIONS.MOST_UPVOTED)}` },
    ],
  },
];

const Group: React.FC<{ group: MenuGroup }> = ({ group }) => (
  <div>
    <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{group.heading}</div>
    <ul className="flex flex-col">
      {group.items.map((item) => (
        <li key={item.to}>
          <NavigationMenuLink asChild>
            <Link
              to={item.to}
              viewTransition
              className={
                item.more
                  ? 'inline-flex min-h-9 items-center gap-1 text-sm text-muted-foreground hover:text-foreground'
                  : 'inline-flex min-h-9 items-center text-[15px] text-foreground hover:text-primary'
              }
            >
              {item.label}
              {item.more ? <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /> : null}
            </Link>
          </NavigationMenuLink>
        </li>
      ))}
    </ul>
  </div>
);

const SequencesMegaMenu: React.FC = () => {
  const navigate = useNavigate();
  const columns: MenuGroup[][] = [holidayColumn(), GENRE_COLUMN, PROPS_COLUMN, QUICK_COLUMN];

  return (
    // `static`: the panel positions against the fixed header, not the trigger,
    // so it can sit centred on the page instead of running off the right edge.
    <NavigationMenu className="static" viewportWrapperClassName="right-0">
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger
            className="font-medium text-foreground bg-transparent rounded-none shadow-none border-none px-2 py-0 h-auto hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors text-base"
            onClick={(e) => {
              // The chevron is an SVG child; a click on the word itself goes to the catalog.
              if (e.target === e.currentTarget) navigate('/sequences');
            }}
          >
            Sequences
          </NavigationMenuTrigger>
          <NavigationMenuContent className="mt-2 rounded-lg border bg-background p-7 shadow-xl md:w-[960px] md:max-w-[94vw] animate-in fade-in slide-in-from-top-1 duration-200 ease-out motion-reduce:[--tw-enter-translate-y:0px]">
            <div className="mb-5 flex items-center justify-between border-b pb-4">
              <span className="text-lg font-semibold">Sequences</span>
              <NavigationMenuLink asChild>
                <Link to="/sequences" viewTransition className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
                  Browse all
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </NavigationMenuLink>
            </div>
            <div className="grid grid-cols-2 gap-x-10 gap-y-6 md:grid-cols-4">
              {columns.map((groups, index) => (
                <div key={index} className="flex flex-col gap-6">
                  {groups.map((group) => (
                    <Group key={group.heading} group={group} />
                  ))}
                </div>
              ))}
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default SequencesMegaMenu;
